import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLock, faSync } from "@fortawesome/free-solid-svg-icons"
import CustomModal from "../basics/interface/CustomModal"
import { COLORS } from "../../utilities/Constants"
import { MESSAGE_FICHE_CLOSE } from "../../utilities/ficheClose"

// UI du verrou de fiche — commune aux trois fiches (dépannage, atelier,
// location) : bandeau lecture seule, bouton « demander la main », et les trois
// modals du cycle de prise de main. Tout ça est né côté atelier
// (Atelier*ModalV2), seul module verrouillé ; le contenu n'a jamais rien eu
// d'atelier. Les modals sont rendus par `useFicheVerrou`, pas directement par
// les fiches ; le bandeau et le bouton se placent dans le header de chaque
// fiche (l'emplacement, lui, est propre à chaque mise en page).

// Bandeau en tête de fiche : quelqu'un d'autre tient le verrou.
export const BandeauLectureSeule = ({ detenuePar }) => (
	<div
		style={{
			display: "flex",
			alignItems: "center",
			gap: 10,
			padding: "12px 18px",
			marginBottom: 20,
			borderRadius: 12,
			backgroundColor: COLORS.warningLight,
			border: `1px solid ${COLORS.gold}`,
			color: COLORS.gold,
			fontSize: 14,
			fontWeight: 500,
		}}
	>
		<FontAwesomeIcon icon={faLock} />
		Lecture seule — fiche verrouillée par {detenuePar}
	</div>
)

// Jordan 30.10 : fiche close, utilisateur non boss (utilities/ficheClose).
// `blocsOuverts` : rôle du tableau, quelques blocs restent modifiables.
// Tant que le bandeau est affiché, une commande désactivée (natif ou par un
// <fieldset disabled>) se VOIT désactivée : les styles inline des boutons V2
// n'ont pas d'état « disabled » (retour planificateur 30.10). Règle limitée à
// la page d'une fiche close, démontée avec le bandeau.
const STYLE_COMMANDES_DESACTIVEES = `
button:disabled, fieldset:disabled button {
	opacity: 0.45 !important;
	cursor: not-allowed !important;
}
input:disabled, select:disabled, textarea:disabled,
fieldset:disabled input, fieldset:disabled select, fieldset:disabled textarea {
	background-color: ${COLORS.bg} !important;
	cursor: not-allowed !important;
}
`

export const BandeauFicheClose = ({ blocsOuverts }) => (
	<div
		style={{
			display: "flex",
			alignItems: "center",
			gap: 10,
			padding: "12px 18px",
			marginBottom: 20,
			borderRadius: 12,
			backgroundColor: COLORS.warningLight,
			border: `1px solid ${COLORS.gold}`,
			color: COLORS.gold,
			fontSize: 14,
			fontWeight: 500,
		}}
	>
		<style>{STYLE_COMMANDES_DESACTIVEES}</style>
		<FontAwesomeIcon icon={faLock} />
		{MESSAGE_FICHE_CLOSE}
		{blocsOuverts ? ` ${blocsOuverts} restent modifiables.` : ""}
	</div>
)

// Cadenas cliquable du header : n'existe QUE sur une fiche tenue par une autre
// session (cliquer = demander la main). Sur une fiche libre, rien — il n'y a
// plus de rituel de fermeture depuis la phase 1.
export const BoutonDemanderMain = ({ detenuePar, onClick }) => (
	<button
		type="button"
		title="Demander la main"
		onClick={onClick}
		style={{
			display: "inline-flex",
			alignItems: "center",
			gap: 6,
			marginTop: 4,
			padding: "4px 10px",
			borderRadius: 8,
			border: "none",
			cursor: "pointer",
			fontFamily: "inherit",
			fontSize: 12,
			fontWeight: 600,
			color: COLORS.danger,
			backgroundColor: COLORS.dangerLight,
		}}
	>
		<FontAwesomeIcon icon={faLock} />
		{`Verrouillée par ${detenuePar}`}
	</button>
)

const Spinner = () => (
	<div
		style={{ display: "flex", justifyContent: "center", padding: "12px 0" }}
	>
		<FontAwesomeIcon icon={faSync} spin size="2x" />
	</div>
)

// Côté DEMANDEUR : machine à états du cycle « demander la main ».
// - confirm  : confirme l'envoi de la demande (+ « Forcer » si admin/boss) ;
// - pending  : demande envoyée, en attente de réponse (spinner + Annuler) ;
// - refused  : le détenteur a refusé ;
// - timeout  : pas de réponse après 30 s (+ « Forcer » si admin/boss).
// `busy` affiche un spinner (POST request/force en cours).
export const DemandeMainModal = ({
	phase,
	holder,
	canForce,
	busy,
	onRequest,
	onCancel,
	onForce,
	onClose,
}) => {
	if (phase === "pending") {
		return (
			<CustomModal
				title="Demande envoyée"
				subtitle={`En attente de la réponse de ${holder}…`}
				onClose={() => {}}
				showCloseButton={false}
				buttons={[
					{
						title: "Annuler",
						styleName: "secondary",
						onClick: onCancel,
					},
				]}
			>
				<Spinner />
			</CustomModal>
		)
	}

	if (phase === "refused") {
		return (
			<CustomModal
				title="Demande refusée"
				subtitle={`${holder} a refusé votre demande. La fiche reste en lecture seule.`}
				onClose={onClose}
				showCloseButton={false}
				buttons={[
					{ title: "Fermer", styleName: "primary", onClick: onClose },
				]}
			/>
		)
	}

	if (phase === "timeout") {
		return (
			<CustomModal
				title="Pas de réponse"
				subtitle={`${holder} n'a pas répondu à votre demande.`}
				onClose={busy ? () => {} : onClose}
				showCloseButton={false}
				showFooter={!busy}
				buttons={
					canForce
						? [
								{
									title: "Fermer",
									styleName: "secondary",
									onClick: onClose,
								},
								{
									title: "Forcer la prise de main",
									styleName: "danger",
									onClick: onForce,
								},
							]
						: [
								{
									title: "Fermer",
									styleName: "primary",
									onClick: onClose,
								},
							]
				}
			>
				{busy && <Spinner />}
			</CustomModal>
		)
	}

	// phase === "confirm"
	return (
		<CustomModal
			title="Demander la main ?"
			subtitle={`La fiche est verrouillée par ${holder}. Une demande lui sera envoyée ; il pourra accepter ou refuser.`}
			onClose={busy ? () => {} : onClose}
			showCloseButton={!busy}
			showFooter={!busy}
			buttons={
				canForce
					? [
							{
								title: "Annuler",
								styleName: "secondary",
								onClick: onClose,
							},
							{
								title: "Forcer",
								styleName: "danger",
								onClick: onForce,
							},
							{
								title: "Demander la main",
								styleName: "primary",
								onClick: onRequest,
							},
						]
					: [
							{
								title: "Annuler",
								styleName: "secondary",
								onClick: onClose,
							},
							{
								title: "Demander la main",
								styleName: "primary",
								onClick: onRequest,
							},
						]
			}
		>
			{busy && <Spinner />}
		</CustomModal>
	)
}

// Reçu par le DÉTENTEUR : une autre session demande la main. Accepter cède le
// verrou (on passe en lecture seule) ; Refuser garde la main. Avertit si des
// modifications non sauvegardées seront perdues à la cession.
export const DemandeRecueModal = ({
	by,
	hasUnsavedChanges,
	pending,
	onAccept,
	onRefuse,
}) => (
	<CustomModal
		title="Demande de prise de main"
		subtitle={
			hasUnsavedChanges
				? `${by} demande la main sur cette fiche. Si vous acceptez, vos modifications non sauvegardées seront perdues.`
				: `${by} demande la main sur cette fiche.`
		}
		onClose={() => {}}
		showCloseButton={false}
		showFooter={!pending}
		buttons={[
			{ title: "Refuser", styleName: "secondary", onClick: onRefuse },
			{ title: "Accepter", styleName: "primary", onClick: onAccept },
		]}
	>
		{pending && <Spinner />}
	</CustomModal>
)

// Notif : une autre session a pris la main sur la fiche que cet onglet éditait.
// Fermeture par interaction uniquement (bouton « Compris ») — pas de clic-overlay,
// pas de toast : on veut une info claire quand on se fait kicker.
export const MainPerdueModal = ({ by, onClose }) => (
	<CustomModal
		title="Vous avez perdu le contrôle de la fiche"
		subtitle={`${by} a pris la main sur la fiche. Vos modifications non sauvegardées sont perdues.`}
		onClose={() => {}}
		showCloseButton={false}
		buttons={[{ title: "Compris", styleName: "primary", onClick: onClose }]}
	/>
)
